@file:OptIn(ExperimentalTime::class)

package com.macrofocus.eflux.ui.layer

import com.macrofocus.eflux.DataLoader
import com.macrofocus.eflux.network.coordinate.Coordinate
import com.macrofocus.eflux.toISO8601
import com.macrofocus.eflux.toInstant
import com.macrofocus.eflux.toRawString
import deckgl.MVTLayer
import js.objects.unsafeJso
import korlibs.time.DateTime
import org.locationtech.jts.geom.GeometryFactory
import org.locationtech.jts.geom.Point
import org.locationtech.jts.io.WKTReader
import react.RefObject
import kotlin.js.Date
import kotlin.time.ExperimentalTime

fun createCoordinateLayer(dataLoader: DataLoader, dateTime: DateTime,
                          riskMask: Int,
                          isCartographicZoomed: Boolean, zoomLevelLinear: Double, isCartographic: Boolean,
                          lastClickTime: RefObject<Double>): MVTLayer<Any> {
    val controller = dataLoader.controller
    val doubleClickDelay = 300 // ms

    val transparent = arrayOf(0, 0, 0, 1)
    val severe = arrayOf(139, 0, 0)
    val serious = arrayOf(255, 69, 0)
    val warning = arrayOf(255, 215, 0)
    val normal = arrayOf(0, 128, 0)
    val invalid = arrayOf(35, 135, 135)
    val vectorLayer = MVTLayer<Any>(unsafeJso {
        id = "coordinate-layer"
        this.data = "/coordinate_at?datetime=${dateTime.toRawString().toInstant().toISO8601()}"
//        asDynamic().minZoom = dataLoader.endZoomLevelMorphing.value

        visible = isCartographic

        opacity = 0.5 // if(isCartographicZoomed) 0.5 else 1.0
        asDynamic().getFillColor = { d: dynamic ->
            val probability = d.properties?.probability
            val risk_map = d.properties?.risk_map as? Int?
            if(isCartographicZoomed) {
                if (risk_map != null && risk_map and riskMask == 0) normal
                else if (probability == null) invalid
                else if (probability >= 0.1) severe
                else if (probability >= 0.05) serious
                else if (probability > 0.0) warning
                else normal
            } else {
                if (risk_map != null && risk_map and riskMask == 0) normal
                else if (probability == null) transparent
                else if (probability >= 0.1) severe
                else if (probability >= 0.05) serious
                else if (probability > 0.0) warning
                else transparent
            }
        }
        asDynamic().filled = true
        asDynamic().getLineColor = { d: dynamic ->
            val probability = d.properties?.probability
            val risk_map = d.properties?.risk_map as? Int?
            if(isCartographicZoomed) {
                if (risk_map != null && risk_map and riskMask == 0) transparent
                else if (probability == null) invalid
                else if (probability >= 0.1) severe
                else if (probability >= 0.05) serious
                else if (probability > 0.0) warning
                else normal
            } else {
                if (risk_map != null && risk_map and riskMask == 0) transparent
                else if (probability == null) transparent
                else if (probability >= 0.1) severe
                else if (probability >= 0.05) serious
                else if (probability > 0.0) warning
                else transparent
            }
        }
        asDynamic().stroked = true
        asDynamic().getPointRadius = { d: dynamic ->
            val probability = d?.properties?.probability
            val radius = if (probability == null) 8
            else if (probability >= 0.5) 16
            else if (probability >= 0.1) 14
            else if (probability >= 0.05) 12
            else if (probability > 0.0) 10
            else 8
            radius * (0.2 + (0.7 * zoomLevelLinear))
        }
        asDynamic().pointRadiusUnits = "pixels"
        updateTriggers = unsafeJso {
            this.asDynamic().opacity = arrayOf(isCartographicZoomed)
            this.asDynamic().getPointRadius = arrayOf(zoomLevelLinear)
            this.asDynamic().getLineColor = arrayOf(isCartographicZoomed, riskMask)
            this.asDynamic().getFillColor = arrayOf(isCartographicZoomed, riskMask)
        }

        pickable = true
        autoHighlight = true
        onHover = { info ->
            val o = info.`object`
            if (o != null) {
                val geometry = info.`object`?.properties?.geometry as? String
                if (geometry != null) {
                    controller.probeCoordinate(createCoordinate(info.`object`?.properties))
                } else {
                    controller.probeCoordinate(null)
                }
            } else {
                controller.probeCoordinate(null)
            }
        }
        onClick = { info: dynamic ->
            val now = Date.now()
            val dbl = now - lastClickTime.current!! < doubleClickDelay
            lastClickTime.current = now

            val o = info.`object`
            if (o != null) {
                val geometry = info.`object`?.properties?.geometry
                if (geometry != null) {
                    val coordinate = createCoordinate(info.`object`?.properties)
                    if(dbl) {
                        dataLoader.physicalZoom(coordinate.physicalGeometry)
                    }
                    controller.selectCoordinate(coordinate)
                } else {
                    controller.selectCoordinate(null)
                }
            } else {
                controller.selectCoordinate(null)
            }
        }
    })
    return vectorLayer
}

private fun createCoordinate(o: dynamic) : Coordinate {
    val geometry = if(o?.geometry != null) {
        WKTReader(GeometryFactory()).read(o.geometry as String)
    } else null

    return Coordinate(
        o?.coordinateId as? String,
        geometry as? Point,
        null,
        o?.seq as? Int,
        o?.probability as? Double,
        o?.risk_map as? Int,
        o?.risk_list as? String,
        o?.logicalBranchIds as? String,
        o?.description as? String,
        o?.t_70m as? Double,
        o?.wind_speed_70m as? Double,
        o?.wind_gusts_70m_1h as? Double,
        o?.precip_5min as? Double,
        o?.precip_type_intensity_5min as? Double,
        o?.forest_fire_warning as? Double,
        o?.power_line_oscillation_24h as? Double,
        o?.weather_code_1h as? Double,
        o?.tstorm_warning_1h as? Double,
        o?.air_density_70m as? Double,
        o?.count as? Double
    )
}
